// 狼人杀, the second game of the 谁是卧底 hut. The office deals the roles and runs the nights and days (src/werewolf.ts);
// this only shows what it sends: the public state to everyone, and to each player their own role and what it lets them
// know. Built from the shared full-screen pieces in games.js (passed in), so it looks like the other games.
import { icon, put } from '/icons.js';
import { t, tParts, has, onLang } from '/i18n.js';
import { voiceNote } from '/game-voice.js';
// icon: the pixel icon's name (public/icons.js). 猎人 and 守卫 are drawn too (bow, shield), for when the office deals them.
// name, tip: catalog keys (public/i18n.js)
const ROLE = {
  wolf: { name: 'ww.role.wolf.name', icon: 'wolf', tip: 'ww.role.wolf.tip' },
  seer: { name: 'ww.role.seer.name', icon: 'crystal', tip: 'ww.role.seer.tip' },
  witch: { name: 'ww.role.witch.name', icon: 'potion', tip: 'ww.role.witch.tip' },
  villager: { name: 'ww.role.villager.name', icon: 'villager', tip: 'ww.role.villager.tip' },
};
const secsLeft = (t) => Math.max(0, Math.ceil((t - Date.now()) / 1000));

export function initWerewolf({ send, myId, shell, el, btn, addLog, coded, scoreList, leaderboard, fulls, render, micOn = () => true }) {
  const W = { phase: 'idle', day: 0, endsAt: 0, seats: [], acked: [], ready: [], voted: [], deaths: [], tally: [], scores: [], gains: [], story: [],
    wolves: 2, you: null, open: false, dismissed: false, poisonMode: false };
  const w = shell('wfull', () => t('ww.title'), () => t('ww.say'), 'wolf'); // functions: shell redraws them on a language change
  const role = el('div', 'wrole'), phaseLine = el('div', 'sphase'), voice = el('div', 'gvoice'), acts = el('div', 'wacts'), seats = el('div', 'sseats');
  const stage = el('div', 'sstage wstage'); stage.append(role, phaseLine, voice, acts, seats, w.card);
  w.card.classList.add('wcard');
  w.stage.append(stage);
  w.form.onsubmit = (e) => { e.preventDefault(); const v = w.input.value.trim(); if (v) send({ t: 'w_chat', text: v }); w.input.value = ''; };
  const full = {
    isOpen: () => W.open,
    close: () => { W.open = false; W.dismissed = true; render(); },
    focus: () => { if (!w.input.disabled) w.input.focus(); },
  };
  fulls.push(full);
  w.leave.onclick = full.close;
  const act = (a, id) => send({ t: 'w_act', act: a, ...(id ? { id } : {}) });

  const seatOf = (id) => W.seats.find((s) => s.id === id);
  const num = (id) => W.seats.findIndex((s) => s.id === id) + 1;
  const label = (id) => { const s = seatOf(id); return s ? t('ww.seat', { n: num(id), name: s.name }) : '?'; };
  const running = () => W.phase !== 'idle' && W.phase !== 'over';
  /** a seat's tags: strings, or [icon, text] */
  const stagOf = (tags) => { const e = el('span', 'stag'); tags.forEach((t, i) => { if (i) e.append(' '); if (Array.isArray(t)) e.append(icon(t[0]), ...(t[1] ? [' ', t[1]] : [])); else e.append(t); }); return e; };

  function wRender() {
    const mine = myId(), y = W.you && W.you.id === mine ? W.you : null, seat = seatOf(mine), alive = !!seat?.alive;
    const r = y && ROLE[y.role], night = W.phase === 'night';
    stage.classList.toggle('night', night);
    w.round.textContent = W.day ? t(night ? 'ww.round.night' : 'ww.round.day', { day: W.day }) : W.phase === 'deal' ? t('ww.round.deal') : '';
    w.who.textContent = W.seats.length ? t('ww.who', { n: W.seats.length, wolves: W.wolves }) : '';
    // Who I am (only ever from my own w_you).
    role.replaceChildren();
    if (y && running()) {
      role.append(el('span', 'slabel', t(alive ? 'ww.you.role' : 'ww.you.roleOut')), put(el('b', 'wrole-' + y.role), r.icon, t(r.name)), el('span', 'wtip', t(r.tip)));
      if (y.team) role.append(el('span', 'wteam', t('ww.you.team', { names: y.team.filter((id) => id !== mine).map(label).join(t('ww.sep')) })));
      if (y.potions) { const pt = el('span', 'wteam'); pt.append(...tParts('ww.you.potions', { save: y.potions.save ? icon('check') : t('ww.you.used'), poison: y.potions.poison ? icon('check') : t('ww.you.used') })); role.append(pt); }
    } else if (running()) role.append(el('span', 'slabel', t('ww.watching')), el('b', 'small', t('ww.joinNext')));
    role.style.display = role.childNodes.length ? '' : 'none';
    // What happens now, and what I can do.
    const step = alive && night ? y?.step : null;
    if (step !== 'witch') W.poisonMode = false;
    let line = '', ic = null; // the phase line and its icon
    acts.replaceChildren();
    if (W.phase === 'deal') {
      line = t(seat ? 'ww.deal.remember' : 'ww.deal.dealing');
      if (seat && alive) acts.append(W.acked.includes(mine) ? el('span', 'wwait', t('ww.deal.acked')) : btn(t('ww.deal.ack'), 'gprimary', () => act('ack')));
    } else if (night) {
      if (step === 'kill') { ic = 'wolf'; line = t(y.done ? 'ww.kill.done' : 'ww.kill.pick'); }
      else if (step === 'check') { ic = 'crystal'; line = t(y.done ? 'ww.check.done' : 'ww.check.pick'); }
      else if (step === 'witch' && !y.done) {
        const v = y.victim;
        ic = 'potion'; line = y.potions.save ? (v ? t('ww.witch.victim', { name: label(v) }) : t('ww.witch.noVictim')) : t('ww.witch.noSave');
        if (W.poisonMode) { ic = 'skull'; line = t('ww.witch.poisonPick'); }
        if (y.potions.save && v && !W.poisonMode) {
          const self = v === mine && !y.selfSave;
          const b = btn(self ? t('ww.witch.noSelfSave') : t('ww.witch.save', { name: label(v) }), 'gprimary', () => act('save', v)); b.disabled = self; acts.append(b);
        }
        if (y.potions.poison) acts.append(btn(t(W.poisonMode ? 'ww.witch.poisonCancel' : 'ww.witch.poison'), '', () => { W.poisonMode = !W.poisonMode; wRender(); }));
        acts.append(btn(t('ww.witch.pass'), '', () => act('pass')));
      } else { ic = 'moon'; line = t(alive ? 'ww.night.sleep' : 'ww.night.out'); }
    } else if (W.phase === 'dawn') { ic = 'sun'; line = W.deaths.length ? t('ww.dawn.deaths', { names: W.deaths.map(label).join(t('ww.sep')) }) : t('ww.dawn.peace'); }
    else if (W.phase === 'day') {
      // the speaking order (werewolf.ts): only the speaker is heard by the table; a page from before had free talk
      const sp = W.speaker;
      ic = 'mic'; line = sp === mine ? t('ww.day.yourTurn') : sp ? t('ww.day.speaker', { who: label(sp) }) : t('ww.day.talk');
      if (sp === mine) acts.append(btn(t('ww.day.done'), 'gprimary', () => act('ready')));
      else if ((sp === undefined || !W.talk?.length) && alive && !W.ready.includes(mine)) acts.append(btn(t('ww.day.ready'), 'gprimary', () => act('ready'))); // (an office from before: free talk)
    } else if (W.phase === 'vote') {
      const myVote = W.myVote;
      line = alive ? (W.voted.includes(mine) ? (myVote ? t('ww.vote.voted', { name: label(myVote) }) : t('ww.vote.abstained')) : t('ww.vote.pick')) : t('ww.vote.out');
      if (!alive || !W.voted.includes(mine)) ic = 'vote';
      if (alive && !W.voted.includes(mine)) acts.append(btn(t('ww.vote.abstain'), '', () => { W.myVote = null; act('abstain'); }));
    } else if (W.phase === 'exile') line = W.exiled ? t('ww.exile.out', { name: label(W.exiled) }) : t('ww.exile.none');
    else if (W.phase === 'over') { line = t(W.aborted ? 'ww.over.aborted' : W.winner === 'wolves' ? 'ww.over.wolves' : W.winner === 'good' ? 'ww.over.good' : 'ww.over.draw'); ic = W.aborted ? null : W.winner === 'wolves' ? 'wolf' : W.winner === 'good' ? 'party' : null; }
    put(phaseLine, ic, line);
    voiceNote(voice, myVoice(seat, y), micOn());
    acts.style.display = acts.childNodes.length ? '' : 'none';
    // The seats: everyone sees alive/out; my role adds what it knows (wolf team and picks, seer's checks, the victim).
    const focused = seats.contains(document.activeElement) ? document.activeElement.dataset.seat : null;
    const picks = y?.picks || {}, team = new Set(y?.team || []), checks = new Map((y?.checks || []).map((c) => [c.id, c.wolf]));
    const reveal = new Map((W.reveal || []).map((x) => [x.id, x.role]));
    const votesFor = new Map(); for (const v of W.tally) if (v.to) votesFor.set(v.to, (votesFor.get(v.to) || 0) + 1);
    seats.replaceChildren();
    W.seats.forEach((o, i) => {
      const can = alive && o.alive && (
        (step === 'kill' && !team.has(o.id)) || (step === 'check' && !y.done && o.id !== mine) ||
        (step === 'witch' && W.poisonMode && o.id !== mine) || (W.phase === 'vote' && !W.voted.includes(mine) && o.id !== mine));
      const b = el('button', 'seat' + (o.alive ? '' : ' out') + (o.id === mine ? ' me' : '') + (W.phase === 'day' && W.speaker === o.id ? ' speaking' : '') + (team.has(o.id) && running() ? ' wolfmate' : '') +
        (picks[mine] === o.id || W.myVote === o.id && W.phase === 'vote' ? ' myvote' : ''));
      const tags = [];
      if (!o.alive) tags.push(t(o.left ? 'ww.tag.left' : 'ww.tag.out'));
      if (W.away?.includes(o.id)) tags.push(t('ww.away')); // dropped off: the seat is held for a while
      if (reveal.has(o.id)) tags.push([ROLE[reveal.get(o.id)].icon, t(ROLE[reveal.get(o.id)].name)]);
      else {
        if (team.has(o.id)) tags.push(['wolf', t('ww.tag.team')]);
        if (checks.has(o.id)) tags.push(['crystal', t(checks.get(o.id) ? 'ww.tag.wolf' : 'ww.tag.good')]);
        const by = Object.entries(picks).filter(([, t]) => t === o.id).map(([f]) => num(f));
        if (by.length) tags.push(['target', by.map((n) => t('ww.seatNo', { n })).join(' ')]);
        if (step === 'witch' && y.victim === o.id) tags.push(['drop', t('ww.tag.attacked')]);
      }
      if (o.alive && W.phase === 'deal' && W.acked.includes(o.id)) tags.push(['check', '']);
      if (o.alive && W.phase === 'day' && W.speaker === o.id) tags.push(['mic', t('ww.day.speaking')]);
      else if (o.alive && W.phase === 'day' && W.ready.includes(o.id)) tags.push(t('ww.day.spoke'));
      if (o.alive && W.phase === 'vote' && W.voted.includes(o.id)) tags.push(t('ww.tag.voted'));
      if (W.phase === 'exile' && votesFor.get(o.id)) tags.push(t('ww.tag.votes', { n: votesFor.get(o.id) }));
      b.append(el('span', 'sav', String(i + 1)), el('span', 'sname', o.id === mine ? t('ww.me', { name: o.name }) : o.name), stagOf(tags));
      b.disabled = !can; b.classList.toggle('votable', can);
      b.onclick = () => {
        if (!can) return;
        if (step === 'kill') act('kill', o.id);
        else if (step === 'check') act('check', o.id);
        else if (step === 'witch') { W.poisonMode = false; act('poison', o.id); }
        else if (W.phase === 'vote') { W.myVote = o.id; act('vote', o.id); wRender(); }
      };
      b.dataset.seat = o.id; seats.appendChild(b);
      if (o.id === focused && !b.disabled) b.focus();
    });
    // The chat box says where a line will go.
    // (as the voice goes: werewolf.ts chat; a spectator, dead or watching, talks to the spectators)
    const v = myVoice(seat, y);
    const ch = !running() ? 'all' : v === 'ghost' ? 'dead' : v === 'wolf' ? 'wolf' : v === 'all' ? 'all' : null;
    w.input.disabled = !ch;
    w.input.placeholder = t(ch === 'dead' ? 'ww.chat.ghost' : ch === 'wolf' ? 'ww.chat.wolf' : ch ? 'ww.chat.all' : v === 'turn' ? 'ww.chat.turn' : v === 'vote' ? 'ww.chat.vote' : 'ww.chat.night');
    scoreList(w.scores, W.scores);
    if (W.phase === 'over' && W.reveal) {
      const title = phaseLine.textContent;
      leaderboard(w.card, t('ww.over.scores'), W.scores, mine, [btn(t('ww.over.again'), 'gprimary', () => send({ t: 'w_start' })), btn(t('ww.over.leave'), '', full.close)]);
      const head = el('div', 'gctitle', title), roles = el('div', 'wroles');
      for (const x of W.reveal) {
        const s = seatOf(x.id), d = el('div', 'wr' + (x.role === 'wolf' ? ' wolf' : '') + (s?.alive ? '' : ' out'));
        d.append(el('span', '', t('ww.seat', { n: num(x.id), name: s?.name ?? '?' })), put(el('b'), ROLE[x.role].icon, t(ROLE[x.role].name)));
        roles.append(d);
      }
      // the story as codes (storyc: ww.log.story.*), or as the office's words (an office from before)
      const story = el('div', 'wstory'), lines = W.storyc?.length === W.story.length ? W.storyc.map((c, i) => coded('ww.log', { ...c, text: W.story[i] })) : W.story;
      for (const line of lines) story.append(el('div', '', line));
      const gains = el('div', 'wgains', W.gains.length ? t('ww.over.gains', { list: W.gains.map((x) => `${x.name} +${x.gain}`).join(t('ww.sep')) }) : t('ww.over.noGains'));
      w.card.firstChild.before(head, roles, story, gains);
      w.card.style.display = '';
    } else w.card.style.display = 'none';
  }

  /** Where my voice goes now (game-voice.js states), by the office's rules (werewolf.ts voiceOf / mayHear). */
  function myVoice(seat, y) {
    if (!running()) return null;
    if (!seat?.alive) return 'ghost';
    if (W.phase === 'night') return y?.role === 'wolf' ? 'wolf' : 'night';
    if (W.phase === 'day') return W.speaker === undefined ? null : W.speaker === seat.id || !W.talk?.length ? 'all' : 'turn';
    return W.phase === 'vote' ? 'vote' : 'all';
  }
  function msg(m) {
    if (m.t === 'w_state') {
      const fresh = m.phase === 'deal' && (W.phase === 'idle' || W.phase === 'over');
      if (fresh) { W.you = null; W.dismissed = false; w.log.replaceChildren(); }
      if (m.phase !== 'vote') W.myVote = null;
      Object.assign(W, m);
      if (m.phase !== 'idle' && !W.dismissed) W.open = true;
      if (m.phase === 'idle') { W.open = false; W.you = null; }
      wRender(); render();
    } else if (m.t === 'w_you') { W.you = m; wRender(); }
    else if (m.t === 'w_msg') { addLog(w.log, m, 'ww.log'); if (m.mine) w.log.lastChild.classList.add('wpriv'); }
    else if (m.t === 'w_chat') { addLog(w.log, { name: m.name, nameIcon: m.ch === 'wolf' ? 'wolf' : m.ch === 'dead' ? 'ghost' : null, text: m.text }); w.log.lastChild.classList.add('wch-' + m.ch); }
    else if (m.t === 'w_err') addLog(w.log, { text: m.reason === 'turn' || m.reason === 'vote' ? t(`chat.game.ww.${m.reason}`) : has(`ww.err.${m.reason}`) ? t(`ww.err.${m.reason}`) : t('ww.err.other') }); // ww.err.rate, ww.err.night
  }
  function tick() {
    if (W.open && running()) { const seat = seatOf(myId()); voiceNote(voice, myVoice(seat, W.you && W.you.id === myId() ? W.you : null), micOn()); } // (M pressed)
    const mine = myId(), y = W.you && W.you.id === mine ? W.you : null;
    const t = W.phase === 'night' ? (y?.step && !y.done ? y.stepEnds : 0) : running() ? W.endsAt : 0;
    put(w.time, W.open && t ? 'timer' : null, W.open && t ? String(secsLeft(t)) : '');
    w.time.classList.toggle('hurry', !!t && secsLeft(t) <= 10);
  }
  onLang(() => wRender()); // (the away marks)
  return { W, root: w.root, msg, tick, running, open: () => { W.open = true; W.dismissed = false; render(); } };
}
